import { getIssueAnalyticsProps } from '@/core/modules/issues/analytics'
import { EIssueSeverity } from '@/core/modules/issues/constants'
import {
    createColumnHelper,
    Row,
    SortingState,
    Table,
} from '@atbay/cyber-components'
import { useDrawer } from '@core/context/DrawerContext'
import { Analytics } from '@core/services/mixpanel'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { formatDate } from '@/core/utils/time'
import { FC, useCallback, useMemo, useState } from 'react'
import { IssueStatus } from '@/ui/components/IssueStatus'
import { StyledCustomTable } from '../CustomTable/CustomTable.styles'
import Typography from '../typography'
import SeverityIcon from '../svg/SeverityIcon'
import { SEVERITY_TEXT_MAPPING } from '@/ui/routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { SeverityCell } from '@/ui/routes/digital-footprint/routes/assets/components/assetsTable/assetsTable.styles'
import { BecIssuesTableEmptyState } from './components/BecIssuesTableEmptyState'
import { Cell, HeaderText, TableContainer } from './BecIssuesTable.styles'
import { BecIssuesTableProps, TBecIssuesTableRow } from './BecIssuesTable.types'

const ROW_HEIGHT = 60
const MAX_VISIBLE_ROWS = 2

const columnHelper = createColumnHelper<TBecIssuesTableRow>()

const BecIssuesTable: FC<BecIssuesTableProps> = ({ issues }) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const { setDrawerState, drawerState } = useDrawer()
    const { issues: sortedIssues, index: sortedIndex } =
        drawerState.contentProps
    const activeIssueId =
        drawerState.isDrawerOpen &&
        drawerState.drawerContentType === 'becIssueDetails'
            ? sortedIssues?.[sortedIndex]?.id
            : undefined

    const openDrawer = useCallback(
        (rows: TBecIssuesTableRow[], index: number) => {
            setDrawerState((prevState) => ({
                ...prevState,
                isDrawerOpen: true,
                drawerContentType: 'becIssueDetails',
                contentProps: { issues: rows, index },
            }))
        },
        [setDrawerState]
    )

    const handleRowClick = useCallback(
        (row: Row<TBecIssuesTableRow>, table: Table<TBecIssuesTableRow>) => {
            const { sortedIndex, items } = getTableRowContext(row, table)
            Analytics.event('SEM::user_clicks_at_issue', {
                ...getIssueAnalyticsProps(row.original, {
                    issueName: row.original.hostname,
                    findingName: row.original.name,
                }),
                origin: 'bec_issues_table',
            })

            openDrawer(items, sortedIndex)
        },
        [openDrawer]
    )

    const columns = useMemo(
        () => [
            {
                ...columnHelper.accessor('severity', {
                    cell: (info) => (
                        <SeverityCell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid={`bec-table-severity-${info.cell.getValue().toLowerCase()}`}
                            className="table-cell-text"
                        >
                            <SeverityIcon severity={info.cell.getValue()} />
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                            >
                                {SEVERITY_TEXT_MAPPING[info.cell.getValue()]}
                            </Typography>
                        </SeverityCell>
                    ),
                    header: () => (
                        <HeaderText
                            minWidth={'80px'}
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Severity
                        </HeaderText>
                    ),
                    enableSorting: true,
                    sortingFn: (a, b) => {
                        return (
                            EIssueSeverity[a.original.severity] -
                            EIssueSeverity[b.original.severity]
                        )
                    },
                }),
                width: '15%',
            },
            {
                ...columnHelper.accessor('hostname', {
                    cell: (info) => (
                        <Cell
                            className="table-cell-text"
                            data-testid="bec-table-hostname"
                            onClick={() => handleRowClick(info.row, info.table)}
                        >
                            <Typography
                                variant="xs-regular"
                                className="table-cell-text"
                            >
                                {info.cell.getValue() || '-'}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <HeaderText
                            minWidth={'160px'}
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            User
                        </HeaderText>
                    ),
                    enableSorting: true,
                }),
                width: '40%',
            },
            {
                ...columnHelper.accessor('status', {
                    cell: (info) => (
                        <Cell
                            className="table-cell-text"
                            data-testid="bec-table-status"
                            onClick={() => handleRowClick(info.row, info.table)}
                        >
                            <IssueStatus
                                status={info.cell.getValue()}
                                testId="bec-table-status-value"
                            />
                        </Cell>
                    ),
                    header: () => (
                        <HeaderText
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Status
                        </HeaderText>
                    ),
                }),
                width: '20%',
            },
            {
                ...columnHelper.accessor('created_at', {
                    cell: (info) => {
                        const value = info.cell.getValue()

                        return (
                            <Cell
                                className="table-cell-text"
                                data-testid="bec-table-created-at"
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                            >
                                <Typography
                                    variant="xs-regular"
                                    className="table-cell-text"
                                >
                                    {value
                                        ? formatDate(value, 'dd MMM yyyy HH:mm')
                                        : '-'}
                                </Typography>
                            </Cell>
                        )
                    },
                    header: () => (
                        <HeaderText
                            minWidth={'120px'}
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            Date detected
                        </HeaderText>
                    ),
                    enableSorting: true,
                    sortingFn: (a, b) => {
                        const aTime = new Date(
                            a.original.created_at || 0
                        ).getTime()
                        const bTime = new Date(
                            b.original.created_at || 0
                        ).getTime()
                        return aTime - bTime
                    },
                }),
                width: '25%',
            },
        ],
        [handleRowClick]
    )

    if (!issues.length) {
        return (
            <TableContainer>
                <BecIssuesTableEmptyState />
            </TableContainer>
        )
    }

    const tableHeight = ROW_HEIGHT * Math.min(issues.length, MAX_VISIBLE_ROWS)

    return (
        <TableContainer>
            <StyledCustomTable
                activeRow={activeIssueId}
                getRowId={(row: TBecIssuesTableRow) => row.id}
                columns={columns}
                data={issues}
                sorting={sorting}
                setSorting={setSorting}
                rowHeight={ROW_HEIGHT}
                tableHeight={tableHeight}
            />
        </TableContainer>
    )
}

export default BecIssuesTable
